<template>
	<div>
		<div
			class="amelipro-checkbox-group d-flex flex-column"
			:class="{
				'flex-md-row align-md-start': horizontalLabel || fullHorizontal,
				'pills': pills,
				'classic': !pills,
			}"
		>
			<div class="d-flex align-center mr-2 amelipro-checkbox-group__label-wrapper">
				<p
					:id="groupId"
					class="d-inline-flex amelipro-checkbox-group__label"
					:class="{
						'mb-1': !fullHorizontal && !pills,
						'mb-0 mb-md-4': fullHorizontal || pills,
						'margin-label': horizontalLabel || fullHorizontal,
					}"
				>
					<slot name="groupLabel">
						{{ groupLabel }}
					</slot>

					<span
						v-if="ariaRequired || multipleRequired"
						aria-hidden="true"
						class="d-inline-flex"
					>
						&nbsp;*
					</span>
					<span
						v-if="ariaRequired || multipleRequired"
						class="d-sr-only"
					>
						&nbsp;Champ obligatoire
					</span>
				</p>

				<slot name="labelInfo" />
			</div>

			<div
				:aria-labelledby="groupId"
				:aria-required="ariaRequired"
				class="amelipro-checkbox-group__wrapper"
				:class="{
					'mt-1 mt-md-0': fullHorizontal && !pills,
					'mt-2': pills
				}"
				role="group"
			>
				<ul
					class="d-flex flex-wrap list-style-none amelipro-checkbox-group__list"
					:class="{
						'flex-column': !pills,
						'flex-md-row align-md-center': !horizontalLabel && (horizontal || fullHorizontal),
					}"
				>
					<li
						v-for="(item, index) in items"
						:key="index"
						class="amelipro-checkbox-group__item"
						:class="{
							'mb-2': pills,
							'mb-4': !pills,
						}"
					>
						<div class="d-flex align-center amelipro-checkbox-group__item__wrapper">
							<label
								class="mr-2 d-inline-flex flex-row align-center text-body-1 amelipro-checkbox-group__item__label"
								:class="{
									'mr-0 mr-md-2': horizontal || fullHorizontal
								}"
							>
								<input
									:id="`${groupId}-input-${index}`"
									v-model="selectedItems"
									:aria-controls="slotSubItem(index) ? `${groupId}-subitem-${index}` : undefined"
									:aria-describedby="checkErrorCondition ? `${groupId}-error ` + item.description : item.description"
									:aria-expanded="ariaExpandedValue(item, index)"
									:checked="item.isChecked"
									class="mr-2"
									:disabled="item.disabled || disabled"
									:name="`${groupId}-name`"
									:required="ariaRequired ? 'true' : undefined"
									type="checkbox"
									:value="item.value"
									@change="emitChangeEvent(item)"
								>
								<span
									:class="{
										'ap-grey--text': !pills && (item.disabled || disabled),
										'ap-blue--text text--darken-1': pills && !checkErrorCondition && !item.disabled && !disabled,
										'ap-error--text amelipro-checkbox-group__item__label__span__error': checkErrorCondition && !item.disabled && !disabled,
										'ap-grey--text text--darken-1': (!pills && !checkErrorCondition && !item.disabled && !disabled) || (pills && (item.disabled || disabled)),
									}"
								>
									{{ item.label }}
								</span>
							</label>

							<slot :name="`append-${index}`" />
						</div>

						<div
							v-if="slotSubItem(index) && item.isChecked"
							:id="`${groupId}-subitem-${index}`"
						>
							<slot :name="`subItem-${index}`" />
						</div>
					</li>
				</ul>
			</div>
		</div>

		<AmeliproMessage
			v-if="checkErrorCondition"
			:id="`${groupId}-error`"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ error }}
			</p>
		</AmeliproMessage>
	</div>
</template>

<script setup lang="ts">
	/* eslint-disable max-lines */
	import { PropType, computed, onMounted, ref, useSlots } from 'vue';
	import { AmeliproCheckboxGroupItem } from './types';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import { IndexedObject } from '../../types';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		fullHorizontal: {
			type: Boolean,
			default: false,
		},
		groupId: {
			type: String,
			required: true,
		},
		groupLabel: {
			type: String,
			required: true,
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		horizontalLabel: {
			type: Boolean,
			default: false,
		},
		items: {
			type: Array as PropType<AmeliproCheckboxGroupItem[]>,
			required: true,
		},
		multipleRequired: {
			type: Boolean,
			default: false,
		},
		multipleRequiredErrorMessage: {
			type: String,
			default: 'Vous devez cocher au moins deux options',
		},
		pills: {
			type: Boolean,
			default: false,
		},
		requiredErrorMessage: {
			type: String,
			default: 'Sélection obligatoire',
		},
	});

	const isAlreadyCheckedOnce = ref(false);
	const selectedItems = ref<IndexedObject<string>[]>([]);

	onMounted(() => {
		props.items.forEach((element) => {
			if (element.isChecked) {
				selectedItems.value.push(element.value as unknown as IndexedObject<string>);
			}
		});
	});

	// TODO: selectedItems ne se met pas à jour si on change les props ?

	const slots = useSlots();
	const slotSubItem = (index: number): boolean => {
		if (props.horizontal || props.fullHorizontal) {
			return false;
		}
		return Boolean(slots[`subItem-${index}`]);
	};

	const ariaExpandedValue = (item: AmeliproCheckboxGroupItem, index: number): 'true' | 'false' | undefined => {
		if (slotSubItem(index)) {
			return item.isChecked ? 'true' : 'false';
		}
		return undefined;
	};

	const emit = defineEmits(['input']);
	const emitChangeEvent = (item: AmeliproCheckboxGroupItem): void => {
		isAlreadyCheckedOnce.value = true;
		item.isChecked = !item.isChecked;

		emit('input', selectedItems.value);
	};

	const isSingleRequiredErrorMessage = () => props.ariaRequired === true && selectedItems.value.length < 1;
	const isMultipleRequiredErrorMessage = () => props.multipleRequired === true && selectedItems.value.length < 2;
	const error = computed<string>(() => {
		if (isAlreadyCheckedOnce.value === true) {
			if (isSingleRequiredErrorMessage()) {
				return props.requiredErrorMessage;
			} else if (isMultipleRequiredErrorMessage()) {
				return props.multipleRequiredErrorMessage;
			}
		}
		return '';
	});
	const checkErrorCondition = computed<boolean>(() => (
		isAlreadyCheckedOnce.value === true &&
		(isSingleRequiredErrorMessage() || isMultipleRequiredErrorMessage())
	));

	const clear = (): void => {
		selectedItems.value = [];
	};

	defineExpose({ clear });

</script>

<style lang="scss" scoped>
 input {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;

	& + span {
		position: relative;
		cursor: pointer;
	}

	&:focus {
		& + span {
			outline: 1px dotted $ap-grey-darken1;
		}
	}

	&:disabled {
		& + span {
			cursor: default;
		}
	}
}
.amelipro-checkbox-group.classic input {

	&+span {
		position: relative;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
		padding-left: 2rem;
		cursor: pointer;

		&::before {
			position: absolute;
			top: 0.125rem;
			left: 0;
			width: 1.5rem;
			height: 1.5rem;
			border-radius: 0.4rem;
			border: 3px solid $ap-blue-darken1;
			background-color: transparent;
			content: "";
		}
	}

	&:checked {

		&+span {
			&::before {
				background-color: $ap-blue-darken1;
			}

			&::after {
				position: absolute;
				top: 0.525rem;
				left: 0.3rem;
				width: 0.9rem;
				height: 0.5rem;
				border-radius: 2px;
				border-bottom: 3px solid $ap-white;
				border-left: 3px solid $ap-white;
				transform: rotate(-50deg);
				content: "";
			}
		}
	}

	&:disabled {

		&+span {
			cursor: default;

			&::before {
				opacity: 0.6;
			}
		}
	}
}

.amelipro-checkbox-group.pills {

	& label {

		&:not(:last-child) {
			@media #{$media-up-sm} {
				margin-right: 16px;
			}
		}

		&:last-child {
			margin-right: 16px;
		}
	}
	& input {

		& + span {
			font-size: $font-size-sm;
			font-weight: $filter-font-weight;
			padding: $filter-padding-y $filter-padding-x;
			border-radius: $filter-radius;
			border: 1px solid $ap-blue-darken1;
			background-color: $ap-white;
			color: $ap-blue-darken1;

			&.amelipro-checkbox-group__item__label__span__error {
				background-color: $ap-red-lighten4;
				border-color: $ap-red;
			}
		}

		&:checked {

			& + span {
				background-color: $ap-blue-lighten3;

				&::after {
					position: absolute;
					top: 1.1rem;
					left: 1.1rem;
					width: 1rem;
					height: 0.5rem;
					border-bottom: 2px solid $ap-blue-darken1;
					border-left: 2px solid $ap-blue-darken1;
					transform: rotate(-50deg);
					content: "";
				}
			}
		}

		&:disabled {

			& + span {
				background-color: $ap-grey-lighten2;
				border-color: $ap-grey;

				&::after {
					border-bottom-color: $ap-grey-darken1;
					border-left-color: $ap-grey-darken1;
				}
			}
		}
	}
}

.amelipro-checkbox-group__label {
	font-weight: $label-font-weight;
}

.margin-label {
	@media #{$media-up-md} {
		margin-top: 3px;
	}
}
</style>
